import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Shield, Target, Activity, Globe, Eye, 
  TrendingUp, Users, Award, CheckCircle2,
  ArrowRight, Building2, Heart, Zap, Code, Brain, Sparkles, Lock, Database
} from 'lucide-react';
import { companyMetrics, flagshipProducts, industries, featuredService } from '../data/mock';

const Home = () => {
  const iconMap = {
    Shield: Shield,
    Target: Target,
    Activity: Activity,
    Globe: Globe,
    Eye: Eye,
    Building2: Building2,
    Heart: Heart,
    Zap: Zap,
    Code: Code,
    Brain: Brain,
    Lock: Lock,
    Database: Database
  };

  return (
    <div className="page-container">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <Badge className="hero-badge">
              AI-Powered Security
            </Badge>
            <div className="company-name-highlight">IrisTechLabs</div>
            <h1 className="hero-title">
              Enterprise-Grade AI Cybersecurity Solutions
            </h1>
            <p className="hero-subtitle">
              CTEM India | AI TRiSM | BAS Platform | TPRM | Autonomous SOC | AI Red Teaming & LLM Security
            </p>
            <p className="hero-description">
              Gartner-aligned, AI-first cybersecurity solutions designed for enterprises, governments, and critical infrastructure. 
              10+ years of expertise protecting organizations with advanced threat detection, continuous exposure management, and autonomous security operations.
            </p>
            <div className="hero-cta">
              <Button asChild size="lg" className="cta-primary">
                <Link to="/contact">Schedule CTEM Assessment</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="cta-secondary">
                <Link to="/products">Explore Products</Link>
              </Button>
            </div>
          </div>
          <div className="hero-image">
            <div className="hero-animation-container">
              {/* Animated Background Circles */}
              <div className="animated-circle circle-1"></div>
              <div className="animated-circle circle-2"></div>
              <div className="animated-circle circle-3"></div>
              
              {/* Animated Grid Lines */}
              <div className="animated-grid">
                <div className="grid-line grid-line-1"></div>
                <div className="grid-line grid-line-2"></div>
                <div className="grid-line grid-line-vertical-1"></div>
              </div>
              
              {/* Network Nodes - Simplified */}
              <div className="network-node node-1"></div>
              <div className="network-node node-2"></div>
              <div className="network-node node-3"></div>
              <div className="network-node node-4"></div>
              <div className="network-node node-5"></div>
              
              {/* Connecting Lines */}
              <svg className="connection-lines" width="100%" height="100%">
                <line className="connection-line line-1" x1="20%" y1="25%" x2="50%" y2="50%" />
                <line className="connection-line line-2" x1="80%" y1="30%" x2="50%" y2="50%" />
                <line className="connection-line line-3" x1="50%" y1="50%" x2="20%" y2="75%" />
                <line className="connection-line line-4" x1="50%" y1="50%" x2="80%" y2="75%" />
              </svg>
              
              {/* Key AI Insight Cards - Strategic Positioning for 7 Cards */}
              <div className="floating-card insight-card card-1">
                <div className="card-icon">🧠</div>
                <div className="card-content">
                  <div className="card-label">AI Detection</div>
                  <div className="card-metric">99.9% Accuracy</div>
                </div>
                <div className="card-pulse"></div>
              </div>
              
              <div className="floating-card insight-card card-2">
                <div className="card-icon">⚡</div>
                <div className="card-content">
                  <div className="card-label">Response Time</div>
                  <div className="card-metric">75% Faster</div>
                </div>
                <div className="card-pulse"></div>
              </div>
              
              <div className="floating-card insight-card card-3">
                <div className="card-icon">🎯</div>
                <div className="card-content">
                  <div className="card-label">Threats Analyzed</div>
                  <div className="card-metric">2.4M Daily</div>
                </div>
                <div className="card-pulse"></div>
              </div>
              
              <div className="floating-card insight-card card-4">
                <div className="card-icon">🔒</div>
                <div className="card-content">
                  <div className="card-label">Automated Actions</div>
                  <div className="card-metric">85% Auto-Fixed</div>
                </div>
                <div className="card-pulse"></div>
              </div>
              
              {/* VALUE-FOCUSED CARDS */}
              <div className="floating-card insight-card card-5">
                <div className="card-icon">💰</div>
                <div className="card-content">
                  <div className="card-label">Security Costs</div>
                  <div className="card-metric">60% Reduced</div>
                </div>
                <div className="card-pulse"></div>
              </div>
              
              <div className="floating-card insight-card card-6">
                <div className="card-icon">⏱️</div>
                <div className="card-content">
                  <div className="card-label">Analyst Time Saved</div>
                  <div className="card-metric">40 hrs/week</div>
                </div>
                <div className="card-pulse"></div>
              </div>
              
              <div className="floating-card insight-card card-7">
                <div className="card-icon">✅</div>
                <div className="card-content">
                  <div className="card-label">Compliance Ready</div>
                  <div className="card-metric">20+ Frameworks</div>
                </div>
                <div className="card-pulse"></div>
              </div>
              
              <div className="floating-card insight-card card-8">
                <div className="card-icon">🔗</div>
                <div className="card-content">
                  <div className="card-label">Vendor Risk</div>
                  <div className="card-metric">80% Faster</div>
                </div>
                <div className="card-pulse"></div>
              </div>
              
              {/* Scanning Effect */}
              <div className="scanning-line"></div>
              
              {/* Threat Status Indicators - Simplified */}
              <div className="threat-indicator indicator-1 severity-high">
                <div className="indicator-dot"></div>
                <div className="indicator-text">Threat Blocked</div>
              </div>
              <div className="threat-indicator indicator-2 severity-low">
                <div className="indicator-dot"></div>
                <div className="indicator-text">Protected</div>
              </div>
              
              {/* Center AI Brain Hub */}
              <div className="center-hub">
                <div className="hub-ring ring-1"></div>
                <div className="hub-ring ring-2"></div>
                <div className="hub-ring ring-3"></div>
                <div className="hub-core">
                  <div className="hub-icon">🧠</div>
                  <div className="hub-text">AI Brain</div>
                </div>
              </div>
              
              {/* Single AI Confidence Meter */}
              <div className="confidence-meter meter-1">
                <div className="meter-label">Model Confidence</div>
                <div className="meter-bar">
                  <div className="meter-fill" style={{width: '98%'}}></div>
                </div>
                <div className="meter-value">98%</div>
              </div>
              
              {/* Data Particles - Reduced */}
              <div className="data-particle particle-1"></div>
              <div className="data-particle particle-2"></div>
              <div className="data-particle particle-3"></div>
              <div className="data-particle particle-4"></div>
              <div className="data-particle particle-5"></div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED: AI Red Teaming & LLM Security - NEW SERVICE HIGHLIGHT */}
      <section className="featured-service-section" data-testid="ai-red-teaming-section">
        <div className="featured-service-content">
          <div className="featured-badge-container">
            <Badge className="featured-badge">
              <Sparkles className="sparkle-icon" size={14} />
              NEW SERVICE
            </Badge>
          </div>
          <div className="featured-grid">
            <div className="featured-text">
              <div className="featured-icon-wrapper">
                <Brain className="featured-main-icon" />
              </div>
              <h2 className="featured-title">{featuredService.name}</h2>
              <p className="featured-tagline">{featuredService.tagline}</p>
              <p className="featured-description">{featuredService.description}</p>
              
              <div className="featured-highlights">
                <h3 className="highlights-title">Key Capabilities:</h3>
                <ul className="highlights-list">
                  {featuredService.features.slice(0, 4).map((feature, idx) => (
                    <li key={idx} className="highlight-item">
                      <CheckCircle2 className="highlight-check" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="featured-keywords">
                {featuredService.seoKeywords.slice(0, 5).map((keyword, idx) => (
                  <Badge key={idx} variant="secondary" className="keyword-badge">
                    {keyword}
                  </Badge>
                ))}
              </div>

              <div className="featured-cta">
                <Button asChild size="lg" className="featured-cta-primary">
                  <Link to="/contact">Request AI Security Assessment</Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="featured-cta-secondary">
                  <Link to="/products#ai-red-team">Learn More</Link>
                </Button>
              </div>
            </div>
            <div className="featured-visual">
              <div className="featured-card-stack">
                <div className="featured-stat-card card-1">
                  <div className="stat-icon">🛡️</div>
                  <div className="stat-content">
                    <div className="stat-label">Prompt Injection</div>
                    <div className="stat-value">Protection</div>
                  </div>
                </div>
                <div className="featured-stat-card card-2">
                  <div className="stat-icon">🔐</div>
                  <div className="stat-content">
                    <div className="stat-label">LLM Security</div>
                    <div className="stat-value">Assessment</div>
                  </div>
                </div>
                <div className="featured-stat-card card-3">
                  <div className="stat-icon">🎯</div>
                  <div className="stat-content">
                    <div className="stat-label">OWASP LLM</div>
                    <div className="stat-value">Top 10 Aligned</div>
                  </div>
                </div>
                <div className="featured-stat-card card-4">
                  <div className="stat-icon">🤖</div>
                  <div className="stat-content">
                    <div className="stat-label">AI Red Team</div>
                    <div className="stat-value">Experts</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Metrics Section */}
      <section className="metrics-section">
        <div className="metrics-content">
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-value">{companyMetrics.mtddReduction}</div>
              <div className="metric-label">MTTD Reduction</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{companyMetrics.falsePositiveReduction}</div>
              <div className="metric-label">False Positive Reduction</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{companyMetrics.threatAccuracy}</div>
              <div className="metric-label">Threat Detection Accuracy</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{companyMetrics.yearsExpertise}</div>
              <div className="metric-label">Years of Expertise</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{companyMetrics.globalClients}</div>
              <div className="metric-label">Global Clients</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{companyMetrics.complianceFrameworks}</div>
              <div className="metric-label">Compliance Frameworks</div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="section">
        <div className="section-content">
          <div className="section-header">
            <h2 className="section-title">What We Do</h2>
            <p className="section-description">
              IrisTechLabs delivers AI-first cybersecurity solutions that provide continuous protection, proactive threat detection, 
              and autonomous security operations for modern enterprises.
            </p>
          </div>
          <div className="features-grid">
            <Card className="feature-card">
              <CardHeader>
                <div className="feature-icon">
                  <Target className="icon" />
                </div>
                <CardTitle>Continuous Exposure Management</CardTitle>
              </CardHeader>
              <CardContent>
                <p>Gartner CTEM-aligned platform for continuous threat monitoring and risk prioritization</p>
              </CardContent>
            </Card>
            <Card className="feature-card">
              <CardHeader>
                <div className="feature-icon">
                  <Shield className="icon" />
                </div>
                <CardTitle>AI-Enhanced Detection</CardTitle>
              </CardHeader>
              <CardContent>
                <p>ML-powered XDR with 99.9% accuracy and 75% faster threat detection</p>
              </CardContent>
            </Card>
            <Card className="feature-card">
              <CardHeader>
                <div className="feature-icon">
                  <Activity className="icon" />
                </div>
                <CardTitle>Breach Simulation</CardTitle>
              </CardHeader>
              <CardContent>
                <p>24/7 automated red teaming validating security controls continuously</p>
              </CardContent>
            </Card>
            <Card className="feature-card">
              <CardHeader>
                <div className="feature-icon">
                  <Eye className="icon" />
                </div>
                <CardTitle>Autonomous SOC</CardTitle>
              </CardHeader>
              <CardContent>
                <p>AI-powered security operations with automated triage and response</p>
              </CardContent>
            </Card>
            <Card className="feature-card">
              <CardHeader>
                <div className="feature-icon">
                  <Globe className="icon" />
                </div>
                <CardTitle>Third-Party Risk (TPRM)</CardTitle>
              </CardHeader>
              <CardContent>
                <p>AI-driven vendor risk assessment with continuous supply chain monitoring</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Flagship Products */}
      <section className="section products-section">
        <div className="section-content">
          <div className="section-header">
            <h2 className="section-title">Flagship AI-Powered Platforms</h2>
            <p className="section-description">
              Enterprise-grade security platforms built on AI-first architecture
            </p>
          </div>
          <div className="products-grid">
            {flagshipProducts.slice(0, 5).map((product) => {
              const IconComponent = iconMap[product.icon];
              if (!IconComponent) return null;
              return (
                <Card key={product.id} className="product-card">
                  <CardHeader>
                    <div className="product-icon">
                      <IconComponent className="icon" />
                    </div>
                    <CardTitle>{product.name}</CardTitle>
                    <CardDescription>{product.tagline}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="product-description">{product.description}</p>
                    <Button asChild variant="link" className="product-link">
                      <Link to={`/products#${product.shortName.toLowerCase()}`}>
                        Learn More <ArrowRight size={16} className="ml-2" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industries Focus */}
      <section className="section industries-section">
        <div className="section-content">
          <div className="section-header">
            <h2 className="section-title">Industries We Serve</h2>
            <p className="section-description">
              Specialized cybersecurity solutions tailored for your industry
            </p>
          </div>
          <div className="industries-grid">
            {industries.map((industry) => {
              const IconComponent = iconMap[industry.icon];
              if (!IconComponent) return null;
              return (
                <Card key={industry.id} className="industry-card">
                  <CardHeader>
                    <div className="industry-icon">
                      <IconComponent className="icon" />
                    </div>
                    <CardTitle>{industry.fullName}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="industry-description">{industry.description}</p>
                    <div className="industry-regulations">
                      {industry.regulations.slice(0, 3).map((reg, idx) => (
                        <Badge key={idx} variant="secondary" className="regulation-badge">
                          {reg}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Analyst & Regulatory Alignment */}
      <section className="section alignment-section">
        <div className="section-content">
          <div className="alignment-grid">
            <div className="alignment-text">
              <h2 className="alignment-title">Gartner-Aligned Technology</h2>
              <p className="alignment-description">
                Our platforms are built on frameworks recognized by leading industry analysts, ensuring you're investing in future-proof security technology.
              </p>
              <div className="alignment-list">
                <div className="alignment-item">
                  <CheckCircle2 className="check-icon" />
                  <div>
                    <h3 className="alignment-item-title">Gartner CTEM Framework</h3>
                    <p className="alignment-item-text">5-stage continuous exposure management</p>
                  </div>
                </div>
                <div className="alignment-item">
                  <CheckCircle2 className="check-icon" />
                  <div>
                    <h3 className="alignment-item-title">AI TRiSM Framework</h3>
                    <p className="alignment-item-text">AI Trust, Risk & Security Management</p>
                  </div>
                </div>
                <div className="alignment-item">
                  <CheckCircle2 className="check-icon" />
                  <div>
                    <h3 className="alignment-item-title">Zero Trust Architecture</h3>
                    <p className="alignment-item-text">Never trust, always verify approach</p>
                  </div>
                </div>
                <div className="alignment-item">
                  <CheckCircle2 className="check-icon" />
                  <div>
                    <h3 className="alignment-item-title">DPDP India Compliance</h3>
                    <p className="alignment-item-text">India's data protection regulation</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="alignment-image">
              <img 
                src="https://images.unsplash.com/photo-1758518731468-98e90ffd7430?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHRlYW18ZW58MHx8fHwxNzcyMTM2ODY2fDA&ixlib=rb-4.1.0&q=85" 
                alt="Professional Team" 
                className="alignment-img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section why-section">
        <div className="section-content">
          <div className="section-header">
            <h2 className="section-title">Why IrisTechLabs?</h2>
            <p className="section-description">
              Competitive advantages that make us the right cybersecurity partner
            </p>
          </div>
          <div className="why-grid">
            <Card className="why-card">
              <CardHeader>
                <TrendingUp className="why-icon" />
                <CardTitle>AI-First Architecture</CardTitle>
              </CardHeader>
              <CardContent>
                <p>Every product built with AI/ML at its core, not bolted on</p>
              </CardContent>
            </Card>
            <Card className="why-card">
              <CardHeader>
                <Award className="why-icon" />
                <CardTitle>Gartner-Aligned</CardTitle>
              </CardHeader>
              <CardContent>
                <p>Technology aligned with leading analyst frameworks and best practices</p>
              </CardContent>
            </Card>
            <Card className="why-card">
              <CardHeader>
                <Users className="why-icon" />
                <CardTitle>10+ Years Expertise</CardTitle>
              </CardHeader>
              <CardContent>
                <p>Decade of enterprise cybersecurity experience across industries</p>
              </CardContent>
            </Card>
            <Card className="why-card">
              <CardHeader>
                <Shield className="why-icon" />
                <CardTitle>India-Focused</CardTitle>
              </CardHeader>
              <CardContent>
                <p>Deep expertise in DPDP, RBI guidelines, and Indian compliance landscape</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2 className="cta-title">Ready to Transform Your Security Posture?</h2>
          <p className="cta-description">
            Schedule a complimentary CTEM assessment and discover how AI-powered cybersecurity can protect your enterprise
          </p>
          <div className="cta-buttons">
            <Button asChild size="lg" className="cta-button-primary">
              <Link to="/contact">Schedule Assessment</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="cta-button-secondary">
              <Link to="/resources">Download Whitepaper</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
